/**
 * Regalías: órdenes del evento hechas con valor 0. Lo que llevan (entradas,
 * bebidas…) se regaló, casi siempre a una empresa patrocinadora o invitada,
 * y el cliente del evento quiere saber cuánto se vendió, cuánto se regaló y
 * a quién. Funciones puras: nada de Angular ni de Firestore.
 *
 * La regla es por el total: toda orden viva con total 0 es regalía, aunque
 * sea anterior a este cambio y venga guardada como efectivo o POS. Las que
 * crea la caja desde ahora llevan además:
 *   payment.type   = 'courtesy'
 *   courtesy       = true
 *   courtesy_company = 'Empresa a la que se regaló'
 */

import { lineName } from '../event-products/product-variants';

/** Tipo de pago con el que la caja guarda una regalía. */
export const COURTESY_PAYMENT = 'courtesy';

/** Etiqueta para las regalías que no traen empresa (anteriores a la captura). */
export const NO_COMPANY_LABEL = 'Sin empresa';

/** Una línea regalada: producto y cantidad (el precio siempre es 0). */
export interface CourtesyLine {
    name: string;
    quantity: number;
}

/** Una regalía tal como se lista en el tablero del cliente. */
export interface CourtesyOrderRow {
    key: string;
    number: string | number;
    date: Date | null;
    /** Empresa tal como la escribió la caja ('' si no la trae). */
    company: string;
    units: number;
    lines: CourtesyLine[];
    /** Zona (barra) desde la que salió y cuenta que la creó, para el detalle. */
    zone_key: string;
    account_key: string;
}

/** Lo regalado a una empresa. */
export interface CourtesyCompanyRow {
    company: string;
    orders: number;
    units: number;
    /** Qué se le regaló, producto por producto, de mayor a menor cantidad. */
    lines: CourtesyLine[];
}

export interface CourtesySummary {
    orders: number;
    units: number;
    /** De mayor a menor cantidad de unidades; "Sin empresa" siempre al final. */
    companies: CourtesyCompanyRow[];
    /** Todas las regalías, de la más reciente a la más antigua. */
    list: CourtesyOrderRow[];
    /** Qué se regaló en total, producto por producto. */
    products: CourtesyLine[];
}

function isLive(order: any): boolean {
    return !!order && order.status !== 'canceled' && order.status !== 'cancelled' && order.deleted !== true;
}

/**
 * Regalía = orden viva con total 0. La marca de la caja (`courtesy` o
 * payment.type) se acepta también, por si algún día una regalía llevara un
 * cargo simbólico.
 */
export function isCourtesyOrder(order: any): boolean {
    if (!isLive(order)) return false;
    if (order.courtesy === true) return true;
    if (order.payment && order.payment.type === COURTESY_PAYMENT) return true;
    return Number(order.total || 0) === 0;
}

/** Empresa a la que se regaló, limpia de espacios; '' si la orden no la trae. */
export function courtesyCompany(order: any): string {
    if (!order) return '';
    const raw = order.courtesy_company !== undefined && order.courtesy_company !== null ? order.courtesy_company : '';
    return String(raw).replace(/\s+/g, ' ').trim();
}

/** Nombre normalizado para agrupar: "Coca Cola" y "coca  cola" son la misma empresa. */
export function companyKey(name: string): string {
    return String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Unidades que lleva la orden (suma de cantidades de sus líneas). */
export function orderUnits(order: any): number {
    if (!order || !Array.isArray(order.bricks)) return 0;
    return order.bricks.reduce((sum: number, line: any) => sum + Number((line && line.quantity) || 1), 0);
}

/**
 * Clave de agrupación de productos: el nombre sin espacios de más ni
 * mayúsculas. Es la misma regla del resumen de ventas, así "Entrada General"
 * vendida y regalada se comparan como un solo producto.
 */
export function productKey(name: string): string {
    return String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Líneas de la orden como producto + cantidad, agrupadas por nombre. */
export function courtesyLines(order: any): CourtesyLine[] {
    if (!order || !Array.isArray(order.bricks)) return [];
    const map: { [key: string]: CourtesyLine } = {};
    order.bricks.forEach((line: any) => {
        if (!line) return;
        // Con su variante: "Squeeze · Whiskey" regalado se cuenta aparte del original.
        const name = lineName(line);
        const key = productKey(name) || 'producto';
        if (!map[key]) map[key] = { name, quantity: 0 };
        map[key].quantity += Number(line.quantity || 1);
    });
    return Object.keys(map).map(k => map[k]).sort((a, b) => b.quantity - a.quantity);
}

/** Fecha de la orden como Date, venga como Timestamp de Firestore, Date o texto. */
export function orderDate(order: any): Date | null {
    const raw = order && order.added_date;
    if (!raw) return null;
    if (raw instanceof Date) return raw;
    if (typeof raw.toDate === 'function') return raw.toDate();
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
}

function accountKeyOf(order: any): string {
    const account = order ? order.account : null;
    if (account && typeof account === 'object') return account.$key || account.id || account.token || '';
    return account ? String(account) : '';
}

function mergeLines(target: CourtesyLine[], lines: CourtesyLine[]) {
    lines.forEach(line => {
        const key = productKey(line.name);
        const found = target.find(t => productKey(t.name) === key);
        if (found) found.quantity += line.quantity;
        else target.push({ name: line.name, quantity: line.quantity });
    });
}

/**
 * Resumen de regalías de un conjunto de órdenes (ya filtradas por vista:
 * todo el evento, una jornada o un rango). Las que no son regalía se
 * ignoran, así que se le puede pasar la lista completa.
 */
export function summarizeCourtesy(orders: any[]): CourtesySummary {
    const list: CourtesyOrderRow[] = [];
    const byCompany: { [key: string]: CourtesyCompanyRow } = {};
    const products: CourtesyLine[] = [];
    let units = 0;

    (orders || []).forEach(order => {
        if (!isCourtesyOrder(order)) return;
        const lines = courtesyLines(order);
        const rowUnits = orderUnits(order);
        const company = courtesyCompany(order);
        list.push({
            key: order.$key || '',
            number: order.number || order.$key || '',
            date: orderDate(order),
            company,
            units: rowUnits,
            lines,
            zone_key: order.event_category_key || '',
            account_key: accountKeyOf(order),
        });
        units += rowUnits;

        const key = companyKey(company);
        if (!byCompany[key]) byCompany[key] = { company: company || '', orders: 0, units: 0, lines: [] };
        byCompany[key].orders += 1;
        byCompany[key].units += rowUnits;
        mergeLines(byCompany[key].lines, lines);
        mergeLines(products, lines);
    });

    list.sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));

    const companies = Object.keys(byCompany).map(k => byCompany[k]);
    companies.forEach(c => c.lines.sort((a, b) => b.quantity - a.quantity));
    companies.sort((a, b) => {
        // Las regalías sin empresa van al final, aunque sean muchas.
        if (!a.company !== !b.company) return a.company ? -1 : 1;
        if (b.units !== a.units) return b.units - a.units;
        if (b.orders !== a.orders) return b.orders - a.orders;
        return a.company.localeCompare(b.company);
    });
    products.sort((a, b) => b.quantity - a.quantity);

    return { orders: list.length, units, companies, list, products };
}

/**
 * Empresas ya usadas en las órdenes cargadas, de la más reciente a la más
 * antigua y sin repetir: la caja las ofrece como atajo para no escribir
 * "Coca Cola" de tres formas distintas.
 */
export function knownCompanies(orders: any[], extra: string[] = []): string[] {
    const sorted = (orders || []).filter(isCourtesyOrder).slice().sort((a, b) => {
        const da = orderDate(a), db = orderDate(b);
        return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
    });
    const seen: { [key: string]: boolean } = {};
    const result: string[] = [];
    const push = (name: string) => {
        const clean = String(name || '').replace(/\s+/g, ' ').trim();
        const key = companyKey(clean);
        if (!key || seen[key]) return;
        seen[key] = true;
        result.push(clean);
    };
    sorted.forEach(order => push(courtesyCompany(order)));
    (extra || []).forEach(push);
    return result;
}
